import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/css/custom.css";

export const DEFAULT_PAGE_SIZE_OPTIONS = [50, 100, 200];
export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_OPTIONS[0];

const getVisiblePages = (currentPage, totalPages) => {
    if (totalPages <= 1) {
        return [1];
    }

    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const siblingCount = 1;
    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages);
    const showLeftEllipsis = leftSibling > 2;
    const showRightEllipsis = rightSibling < totalPages - 1;
    const pages = [];

    if (!showLeftEllipsis && showRightEllipsis) {
        const leftItemCount = 3 + 2 * siblingCount;
        for (let page = 1; page <= leftItemCount; page += 1) {
            pages.push(page);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
    } else if (showLeftEllipsis && !showRightEllipsis) {
        pages.push(1);
        pages.push("ellipsis");
        const rightItemCount = 3 + 2 * siblingCount;
        for (let page = totalPages - rightItemCount + 1; page <= totalPages; page += 1) {
            pages.push(page);
        }
    } else if (showLeftEllipsis && showRightEllipsis) {
        pages.push(1);
        pages.push("ellipsis");
        for (let page = leftSibling; page <= rightSibling; page += 1) {
            pages.push(page);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
    } else {
        for (let page = 1; page <= totalPages; page += 1) {
            pages.push(page);
        }
    }

    return pages;
};

const PaginationComponent = ({
    currentPage,
    totalPages,
    pageSize,
    totalRecords,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onPageChange,
    onPageSizeChange,
}) => {
    if (!totalRecords) {
        return null;
    }

    const safeTotalPages = Math.max(totalPages, 1);
    const isFirstPage = currentPage <= 1;
    const isLastPage = currentPage >= safeTotalPages;
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const visiblePages = getVisiblePages(currentPage, safeTotalPages);

    const handlePageSizeChange = (event) => {
        const nextPageSize = Number(event.target.value);
        if (onPageSizeChange) {
            onPageSizeChange(nextPageSize);
        }
    };

    const renderPageSizeSelect = (className = "") => (
        <div className={`custom-pagination__page-size ${className}`.trim()}>
            <label className="custom-pagination__page-size-label mb-0 mr-2" htmlFor="pagination-page-size">
                Rows per page
            </label>
            <select
                id="pagination-page-size"
                className="custom-pagination__page-size-select form-control-sm border border-primary form-select"
                value={pageSize}
                onChange={handlePageSizeChange}
            >
                {pageSizeOptions.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </div>
    );

    return (
        <div className="custom-pagination custom-pagination--toolbar">
            <div className="custom-pagination__desktop d-none d-md-flex">
                <div className="custom-pagination__pages">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(1)}
                        disabled={isFirstPage}
                        aria-label="First page"
                    >
                        <i className="fa-solid fa-angles-left" aria-hidden="true"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={isFirstPage}
                        aria-label="Previous page"
                    >
                        <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                    </button>

                    <div className="custom-pagination__info">
                        Showing {startRecord}&ndash;{endRecord} of {totalRecords}
                    </div>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={isLastPage}
                        aria-label="Next page"
                    >
                        <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(safeTotalPages)}
                        disabled={isLastPage}
                        aria-label="Last page"
                    >
                        <i className="fa-solid fa-angles-right" aria-hidden="true"></i>
                    </button>
                </div>

                {renderPageSizeSelect()}
            </div>

            <div className="custom-pagination__mobile d-md-none">
                <div className="custom-pagination__mobile-nav">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={isFirstPage}
                        aria-label="Previous page"
                    >
                        <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                    </button>

                    <span className="custom-pagination__mobile-label">
                        Page {currentPage} of {safeTotalPages}
                    </span>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={isLastPage}
                        aria-label="Next page"
                    >
                        <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>

                {renderPageSizeSelect("custom-pagination__page-size--mobile")}
            </div>
        </div>
    );
};

export default PaginationComponent;
